import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, Checkbox, Grid, LinearProgress} from "@material-ui/core";
import {useParams} from "react-router-dom";
import api, {apiEndpoint} from "../../../../../plugins/api";
import PagePaper from "../../../../../components/PagePaper";
import {useSnackbar} from "notistack";
import {Dashboard, useUppy} from "@uppy/react";
import Uppy from "@uppy/core";
import Turkish from "@uppy/locales/lib/tr_TR";
import XHRUpload from "@uppy/xhr-upload";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import {useTranslation} from "react-i18next";
import { useSelector } from "react-redux";
import { entitySelectors } from "store/slicers/customer/claim";
import UploadSlider from "components/UploadSlider";
import UploadDialog from "components/UploadDialog";
import EditMediaType from "components/EditMediaType";
import sortUploads, { preserveUploadOrder } from "service/sortUploads";

const Item = (props) => {
  const {item, onItemClick, mediaTypes, selectedItemIds, onToggle, onMediaTypeSelect} = props;
  const routeParams = useParams();
  const claim = useSelector((state) => {
    return entitySelectors.selectById(state, routeParams.claim_id);
  });

  return <Grid item xs={12} sm={6} md={3}>
    <Box display={"flex"} flexDirection={"column"} alignItems={"center"}
         style={{
           border: "1px solid lightgray",
           padding: "2px",
           position: "reletive",
           overflow: "hidden"
         }}>
      <button
        type="button"
        onClick={() => {
          onItemClick(item.upload);
        }}
        style={{
          border: 0,
          background: "transparent",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <img
          alt={item.upload.name || "Hasar dosyası"}
          src={item.upload.mimeType.includes("image") ? item.upload.url : "/ui/img/document.png"}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "300px",
            height: "220px",
            objectFit: "contain",
            scale: item.upload.mimeType.includes("image") ? "none" : "1.6"
          }}
        />
      </button>
      <Box display={"flex"} width={"100%"}>
        <Checkbox
          color={"primary"}
          disabled={claim.statusCloseDisabled || claim.isCallCenterUser}
          onChange={() => {
            onToggle(item.upload.id);
          }}
          checked={selectedItemIds.includes(item.upload.id)}
        />
        <Box flexGrow={1} style={{marginRight: "8px"}}>
          <EditMediaType
            mediaTypes={mediaTypes}
            defaultValue={item.mediaType?.id}
            onMediaTypeSelect={(mediaType_id) => {
              onMediaTypeSelect(item.upload.id, mediaType_id)
            }}
            disabled={claim.statusCloseDisabled || claim.isCallCenterUser}
          />
        </Box>
      </Box>
    </Box>
  </Grid>
};

const ItemsGrid = (props) => {
  const {items, deleteItems, onMediaTypeSelect} = props;
  const [imageDialogVisible, setImageDialogVisible] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const {t} = useTranslation();
  const routeParams = useParams();
  const [uploadSliderVisible, setUploadSliderVisible] = useState(false);
  const [images, setImages]= useState([]);
  const claim = useSelector((state) => {
    return entitySelectors.selectById(state, routeParams.claim_id);
  });

  const loadMediaTypes = useCallback(async () => {
    const result = await api.get("/generic/upload-media-types", {
      params: { customer_id: routeParams.customer_id },
    });
    setMediaTypes(result.data);
  }, [routeParams.customer_id]);

  useEffect(() => {
    loadMediaTypes();
  }, [loadMediaTypes]);

  const onItemClick = (upload) => {
    const mimeTypes = ["application/pdf"];
    if (upload.mimeType.includes("image")) {
      const imageList = items
        .map((item) => item.upload)
        .filter((upload) => upload.mimeType.includes("image"))
        .map((upload) => upload.link);
      setImages(imageList)
      setSelectedUpload(upload);
      setImageDialogVisible(true);
      return;
    } else if (mimeTypes.includes(upload.mimeType)) {
      setUploadSliderVisible(true);
      setSelectedUpload(upload);
      return;
    }
    const newWindow = window.open(upload.url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  }

  const toggleSelectedItemId = (item_id) => {
    let newArray = [...selectedItemIds, item_id];
    if (selectedItemIds.includes(item_id)) {
      newArray = newArray.filter(id => id !== item_id);
    }
    setSelectedItemIds(newArray);
  }

  return <>
    {uploadSliderVisible && (
      <UploadSlider
        open={uploadSliderVisible}
        onClose={() => {
          setUploadSliderVisible(false);
        }}
        uploads={items}
        initialUpload={selectedUpload}
      />
    )}
    {imageDialogVisible && (
      <UploadDialog
        open={imageDialogVisible}
        onClose={() => {
          setImageDialogVisible(false);
        }}
        images={images}
        image={selectedUpload}
      />
    )}
    <Box mb={3}>
      {selectedItemIds.length > 0 &&
        <Button
          variant="contained"
          color="primary"
          disabled={claim.statusCloseDisabled || claim.isCallCenterUser}
          onClick={() => {
            deleteItems(selectedItemIds)
          }}>
          {t("button.deleteSelect")}
        </Button>
      }
    </Box>
    <Grid container spacing={3}>
      {items.map((item) => {
        return <Item
          item={item}
          onItemClick={onItemClick}
          mediaTypes={mediaTypes}
          selectedItemIds={selectedItemIds}
          onToggle={toggleSelectedItemId}
          key={item.upload.id}
          onMediaTypeSelect={onMediaTypeSelect}
        />
      })}
    </Grid>
  </>
}

const AllUploads = (props) => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const routeParams = useParams();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const uppy = useUppy(() => {
    return new Uppy({locale: Turkish}).use(XHRUpload, {
      endpoint: `${apiEndpoint}customers/${routeParams.customer_id}/claims/${routeParams.claim_id}/uploads/`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
  });
  const claim = useSelector((state) => {
    return entitySelectors.selectById(state, routeParams.claim_id);
  });

  const loadItems = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    const result = await api.get(`/customers/${routeParams.customer_id}/claims/${routeParams.claim_id}/all-uploads`)
    setItems((currentItems) => {
      if (currentItems.length === 0) {
        return sortUploads(result.data);
      }

      return preserveUploadOrder(currentItems, result.data, (item) => item.upload.id);
    });

    if (showLoading) {
      setLoading(false);
    }
  }, [routeParams.claim_id, routeParams.customer_id]);

  const deleteItems = async (item_ids) => {
    const snackbarKey = enqueueSnackbar(t("snackbar.deleting"));
    const result = await api.delete(
      `/customers/${routeParams.customer_id}/claims/${routeParams.claim_id}/all-uploads`,
      {data: item_ids}
    )
    closeSnackbar(snackbarKey);
    await loadItems(false);
  }

  const updateMediaType = async (upload_id, mediaType_id) => {
    const snackbarKey = enqueueSnackbar(t("snackbar.save"));
    await api.post(
      `/customers/${routeParams.customer_id}/claims/${routeParams.claim_id}/update-media-type`,
      {upload_id, mediaType_id}
    )
    closeSnackbar(snackbarKey);
    await loadItems(false);
  }

  useEffect(() => {
    loadItems()
  }, [loadItems]);

  if (loading) {
    return <LinearProgress/>
  }

  return <>
    <PagePaper>
      <Dashboard
        uppy={uppy}
        proudlyDisplayPoweredByUppy={false}
        height={200}
        width={500}
        disabled={claim.statusCloseDisabled || claim.isCallCenterUser}
        doneButtonHandler={loadItems}
      />
      <Box m={3}/>
      <ItemsGrid items={items} deleteItems={deleteItems} onMediaTypeSelect={updateMediaType}/>
    </PagePaper>
  </>
}

export default AllUploads;
